import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { Shield, Users, Globe, Award } from 'lucide-react'
import { Container } from '@/components/layout'
import { SEO } from '@/components/SEO'
import { supabase } from '@/lib/supabase'

interface AboutContent {
  hero_title: string
  hero_description: string
  section_title: string
  section_content: string
  image_url: string
  stats: { value: string; label: string }[]
}

const defaultAboutContent: AboutContent = {
  hero_title: 'About EngiMarineX',
  hero_description: 'A leading provider of marine equipment solutions, delivering high-quality products to the maritime industry worldwide.',
  section_title: 'Your Trusted Partner in Marine Equipment',
  section_content: '<p>EngiMarineX specializes in the design, manufacturing, and supply of premium marine equipment.</p>',
  image_url: '',
  stats: [
    { value: '20+', label: 'Years Experience' },
    { value: '500+', label: 'Projects Completed' },
    { value: '50+', label: 'Countries Served' },
    { value: '100%', label: 'Certified Products' },
  ],
}

const values = [
  {
    icon: Shield,
    title: 'Quality First',
    description: 'Every product meets the highest international standards and certifications.',
  },
  {
    icon: Users,
    title: 'Customer Focus',
    description: 'We work closely with our clients to deliver tailored solutions.',
  },
  {
    icon: Globe,
    title: 'Global Reach',
    description: 'Serving the maritime industry worldwide with reliable delivery.',
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'Committed to continuous improvement and innovation.',
  },
]

export function AboutPage() {
  const { data: aboutContent } = useQuery({
    queryKey: ['about-content'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'about_content')
        .single()
      if (error) return defaultAboutContent
      return (data?.value as AboutContent) || defaultAboutContent
    },
  })

  const content = aboutContent || defaultAboutContent
  return (
    <>
      <SEO pageKey="about" fallback={{ title: 'About EngiMarineX - Your Trusted Marine Equipment Partner' }} />
      {/* Hero */}
      <section className="bg-secondary-900 text-white py-16 lg:py-24">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">{content.hero_title}</h1>
            <p className="text-lg text-secondary-300 leading-relaxed">
              {content.hero_description}
            </p>
          </motion.div>
        </Container>
      </section>

      {/* About Content */}
      <section className="py-16 lg:py-24">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-secondary-900 mb-6">
                {content.section_title}
              </h2>
              <div
                className="space-y-4 text-secondary-600 prose prose-secondary max-w-none"
                dangerouslySetInnerHTML={{ __html: content.section_content }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-secondary-100 rounded-2xl aspect-square flex items-center justify-center overflow-hidden"
            >
              {content.image_url ? (
                <img
                  src={content.image_url}
                  alt="Company"
                  className="w-full h-full object-cover"
                />
              ) : (
                <p className="text-secondary-400 text-sm">Company Image</p>
              )}
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Stats */}
      {content.stats && content.stats.length > 0 && (
        <section className="py-16 bg-primary-600 text-white">
          <Container>
            <div className={`grid grid-cols-2 lg:grid-cols-${Math.min(content.stats.length, 4)} gap-8`}>
              {content.stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-4xl lg:text-5xl font-bold mb-2">{stat.value}</div>
                  <div className="text-primary-100">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* Values */}
      <section className="py-16 lg:py-24">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-secondary-900 mb-4">
              Our Values
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-100 text-primary-600 rounded-xl mb-4">
                  <value.icon className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-secondary-600 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>
    </>
  )
}
